import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, Modal, PillButton, SegmentedControl } from '../../ui';
import { ReactComponent as CreditCardIcon } from '../../assets/icons/credit-card.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check.svg';
import { ReactComponent as ExternalLinkIcon } from '../../assets/icons/external-link.svg';
import { ReactComponent as AlertTriangleIcon } from '../../assets/icons/alert-triangle.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-cw.svg';
import './AdminLayout.css';

const PLANS = {
  team: { priceMonth: 4, priceYear: 38 },
  business: { priceMonth: 8, priceYear: 77 },
};

function Billing() {
  const { t } = useTranslation();
  const { org, has, seatsUsed, orgId, reloadOrg } = useOutletContext();
  const canManage = has('billing.manage');

  const [interval, setIntervalValue] = useState('month');
  const [seats, setSeats] = useState(Math.max(org?.seats || 1, seatsUsed || 1));
  const [selectedPlan, setSelectedPlan] = useState(org?.plan === 'business' ? 'business' : 'team');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notConfigured, setNotConfigured] = useState(false);

  // Suscripción activa (solo si la org ya pagó). null = sin suscripción.
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(!!org?.stripeSubscriptionId);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [newSeats, setNewSeats] = useState(org?.seats || 1);
  const [subBusy, setSubBusy] = useState(false);
  const [seatsSaved, setSeatsSaved] = useState(false);

  const loadSubscription = useCallback(async () => {
    if (!org?.stripeSubscriptionId) { setSubscription(null); setSubLoading(false); return; }
    setSubLoading(true);
    try {
      const res = await api.get(`/org/${orgId}/billing/subscription`);
      setSubscription(res.data?.subscription || null);
      if (res.data?.subscription?.seats) setNewSeats(res.data.subscription.seats);
    } catch (err) {
      // 404 no_subscription: se muestra el selector de plan sin la tarjeta.
      setSubscription(null);
      if (err.response?.status === 503) setNotConfigured(true);
      else if (err.response?.status !== 404) setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setSubLoading(false);
    }
  }, [org?.stripeSubscriptionId, orgId, t]);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  const subscriptionAction = async (path, body) => {
    setSubBusy(true);
    setError('');
    setSeatsSaved(false);
    try {
      const res = await api.post(`/org/${orgId}/billing/${path}`, body);
      if (res.data?.subscription) setSubscription(res.data.subscription);
      reloadOrg?.();
      return true;
    } catch (err) {
      if (err.response?.status === 503) setNotConfigured(true);
      else if (err.response?.status === 404) setSubscription(null);
      else setError(err.response?.data?.error || t('errors.generic'));
      return false;
    } finally {
      setSubBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (await subscriptionAction('cancel')) setCancelOpen(false);
  };

  const saveSeats = async () => {
    if (await subscriptionAction('seats', { seats: newSeats })) setSeatsSaved(true);
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
  const minSeats = Math.max(1, seatsUsed || 1);

  const checkout = async () => {
    setLoading(true);
    setError('');
    setNotConfigured(false);
    try {
      const base = window.location.origin;
      const res = await api.post(`/org/${orgId}/billing/checkout`, {
        plan: selectedPlan,
        interval,
        seats: Math.max(seats, seatsUsed || 1),
        successUrl: `${base}/admin/${orgId}/billing?success=1`,
        cancelUrl: `${base}/admin/${orgId}/billing?canceled=1`,
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      if (err.response?.status === 503) setNotConfigured(true);
      else setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    setError('');
    setNotConfigured(false);
    try {
      const res = await api.post(`/org/${orgId}/billing/portal`);
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      if (err.response?.status === 503) setNotConfigured(true);
      else setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return <p className="sgc-admin__muted">{t('admin.noPermission')}</p>;
  }

  return (
    <div>
      <h2 className="sgc-admin__section-title">{t('admin.tabs.billing')}</h2>

      <Card className="sgc-admin__form">
        <div className="sgc-admin__row-actions">
          <CreditCardIcon width={20} height={20} aria-hidden="true" />
          <span>{t(`admin.plans.${org.plan}`, org.plan)} · {t(`admin.status.${org.status}`, org.status)}</span>
        </div>
        {org.status === 'trial' && org.trialEndsAt && (
          <p className="sgc-admin__muted">{t('admin.billing.trialEnds', { date: new Date(org.trialEndsAt).toLocaleDateString() })}</p>
        )}
        <p className="sgc-admin__muted">{t('admin.billing.seats', { used: seatsUsed, total: org.seats })}</p>
        {org.stripeCustomerId && !subscription && (
          <PillButton size="sm" variant="secondary" icon={ExternalLinkIcon} onClick={openPortal} disabled={loading}>
            {t('admin.billing.managePlan')}
          </PillButton>
        )}
      </Card>

      {subLoading && <p className="sgc-admin__muted">{t('common.loading')}</p>}

      {subscription && (
        <>
          <h3 className="sgc-admin__section-title">{t('admin.billing.currentSubscription')}</h3>
          <Card className="sgc-admin__form">
            {subscription.cancelAtPeriodEnd && (
              <div className="sgc-admin__row-actions">
                <AlertTriangleIcon width={18} height={18} aria-hidden="true" />
                <span>{t('admin.billing.cancelScheduled', { date: formatDate(subscription.currentPeriodEnd) })}</span>
                <PillButton size="sm" icon={RefreshIcon} onClick={() => subscriptionAction('resume')} disabled={subBusy}>
                  {t('admin.billing.resume')}
                </PillButton>
              </div>
            )}
            <p className="sgc-admin__muted">
              {t('admin.billing.subPlan')}: <strong>{t(`admin.plans.${subscription.plan}`, subscription.plan)}</strong>
              {' · '}{t('admin.billing.subStatus')}: {t(`admin.billing.subStatuses.${subscription.status}`, subscription.status)}
            </p>
            <p className="sgc-admin__muted">
              {t('admin.billing.subSeats', { count: subscription.seats })}
              {subscription.amountPerSeat != null && (
                <> · {subscription.amountPerSeat.toFixed(2)} {subscription.currency}/{t(`admin.billing.per${subscription.interval === 'year' ? 'UserYear' : 'UserMonth'}`)}</>
              )}
            </p>
            <p className="sgc-admin__muted">
              {subscription.cancelAtPeriodEnd
                ? t('admin.billing.endsOn', { date: formatDate(subscription.currentPeriodEnd) })
                : t('admin.billing.renewsOn', { date: formatDate(subscription.currentPeriodEnd) })}
            </p>
            {subscription.stale && <p className="sgc-admin__muted">{t('admin.billing.stale')}</p>}

            <div className="sgc-admin__seats">
              <label htmlFor="billing-change-seats">{t('admin.billing.changeSeats')}</label>
              <input
                id="billing-change-seats"
                type="number"
                min={minSeats}
                max={10000}
                value={newSeats}
                onChange={(e) => { setSeatsSaved(false); setNewSeats(Math.max(minSeats, parseInt(e.target.value, 10) || minSeats)); }}
              />
              <PillButton size="sm" onClick={saveSeats} disabled={subBusy || newSeats === subscription.seats || newSeats < minSeats}>
                {t('common.save')}
              </PillButton>
              <span className="sgc-admin__muted">
                {seatsSaved ? t('admin.billing.seatsSaved') : t('admin.billing.seatsHint', { used: seatsUsed || 0 })}
              </span>
            </div>

            <div className="sgc-admin__row-actions">
              {!subscription.cancelAtPeriodEnd && (
                <PillButton size="sm" variant="danger" onClick={() => setCancelOpen(true)} disabled={subBusy}>
                  {t('admin.billing.cancel')}
                </PillButton>
              )}
              <PillButton size="sm" variant="secondary" icon={ExternalLinkIcon} onClick={openPortal} disabled={loading}>
                {t('admin.billing.openPortal')}
              </PillButton>
            </div>
          </Card>

          <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title={t('admin.billing.cancelTitle')}>
            <div className="sgc-admin__form">
              <p>{t('admin.billing.cancelConfirm', { date: formatDate(subscription.currentPeriodEnd) })}</p>
              <div className="sgc-admin__row-actions">
                <PillButton variant="secondary" onClick={() => setCancelOpen(false)} disabled={subBusy}>
                  {t('admin.billing.keep')}
                </PillButton>
                <PillButton variant="danger" onClick={confirmCancel} disabled={subBusy}>
                  {subBusy ? t('common.loading') : t('admin.billing.cancelConfirmButton')}
                </PillButton>
              </div>
            </div>
          </Modal>
        </>
      )}

      {notConfigured && <p className="sgc-admin__error">{t('admin.billing.notConfigured')}</p>}
      {error && <p className="sgc-admin__error">{error}</p>}

      {!subscription && !subLoading && (<>
      <h3 className="sgc-admin__section-title">{t('admin.billing.choosePlan')}</h3>
      <div className="sgc-admin__seats">
        <label htmlFor="billing-seats">{t('admin.billing.seatsLabel')}</label>
        <input id="billing-seats" type="number" min={Math.max(1, seatsUsed || 1)} max={10000} value={seats} onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value, 10) || 1))} />
        <span className="sgc-admin__muted">{t('admin.billing.seatsHint', { used: seatsUsed || 0 })}</span>
      </div>
      <SegmentedControl
        options={[{ value: 'month', label: t('admin.billing.monthly') }, { value: 'year', label: t('admin.billing.yearly') }]}
        value={interval}
        onChange={setIntervalValue}
      />

      <div className="sgc-admin__plan-cards">
        {Object.entries(PLANS).map(([key, plan]) => (
          <Card
            key={key}
            className={`sgc-admin__plan-card ${selectedPlan === key ? 'sgc-admin__plan-card--selected' : ''}`}
            onClick={() => setSelectedPlan(key)}
          >
            <strong>{t(`admin.plans.${key}`)}</strong>
            <span className="sgc-admin__plan-price">
              {interval === 'month' ? plan.priceMonth : plan.priceYear}€
              <span style={{ fontSize: 13, fontWeight: 400 }}>/{t(`admin.billing.per${interval === 'month' ? 'UserMonth' : 'UserYear'}`)}</span>
            </span>
            <ul className="sgc-admin__plan-checks">
              {t(`admin.plans.${key}Checks`, { returnObjects: true, defaultValue: [] }).map?.((check, i) => (
                <li key={i}><CheckIcon width={14} height={14} aria-hidden="true" /> {check}</li>
              )) || null}
            </ul>
          </Card>
        ))}
      </div>

      <PillButton onClick={checkout} disabled={loading}>
        {loading ? t('common.loading') : t('admin.billing.subscribe')}
      </PillButton>
      </>)}
    </div>
  );
}

export default Billing;
